// src/components/Profile/Profile.tsx
import React, { useEffect, useState } from "react";
import { useUserContext } from "../../UserContext";
import SignOut from "../SignOut/SignOut";
import { useNavigate, useParams } from "react-router-dom";
import { uploadImageToS3 } from "../../utils/ImageUpload";
import { updateUser, updatePost } from "../../graphql/mutations";
import { listPosts, getUser } from "../../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { remove } from "aws-amplify/storage";
import Post from "../Posts/Post";
import Avatar from "../UserAvatar/Avatar";
import "./Profile.scss";

const client = generateClient();

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useUserContext();
  const [viewedUser, setViewedUser] = useState<any>(null);
  const [userPosts, setUserPosts] = useState<any[]>([]);
  const [usernameInput, setUsernameInput] = useState("");
  const [hobbiesInput, setHobbiesInput] = useState("");
  const [colorsInput, setColorsInput] = useState("");
  const [animalInput, setAnimalInput] = useState("");
  const [booksInput, setBooksInput] = useState("");
  const [showsInput, setShowsInput] = useState("");
  const [moviesInput, setMoviesInput] = useState("");
  const [gamesInput, setGamesInput] = useState("");
  const [snacksInput, setSnacksInput] = useState("");
  const [ridesInput, setRidesInput] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);

  const isOwnProfile = !userId || userId === user?.id;

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        await getCurrentUser();
      } catch {
        navigate("/");
      }
    };
    checkAuthState();
  }, [navigate]);

  useEffect(() => {
    const fetchProfileUser = async () => {
      try {
        const result: any = await client.graphql({
          query: getUser,
          variables: { id: isOwnProfile ? user?.id : userId },
        });
        const profileUser = result.data.getUser;
        setViewedUser(profileUser);
        setUsernameInput(profileUser?.username || "");
        setHobbiesInput(profileUser?.hobbies?.join(", ") || "");
        setAnimalInput(profileUser?.favoriteAnimal || "");
        setColorsInput(profileUser?.favoriteColors?.join(", ") || "");
        setBooksInput(profileUser?.favoriteBooks || "");
        setShowsInput(profileUser?.favoriteTvShows || "");
        setMoviesInput(profileUser?.favoriteMovies || "");
        setGamesInput(profileUser?.favoriteGames || "");
        setSnacksInput(profileUser?.favoriteSnacks || "");
        setRidesInput(profileUser?.favoriteDisneyRides || "");
      } catch (err) {
        console.error("Error fetching profile user:", err);
      }
    };

    if (user || !isOwnProfile) {
      fetchProfileUser();
    }
  }, [user, userId, isOwnProfile]);

  useEffect(() => {
    const fetchUserPosts = async () => {
      const id = isOwnProfile ? user?.id : userId;
      if (!id) return;
      try {
        const response: any = await client.graphql({
          query: listPosts,
          variables: {
            filter: { userId: { eq: id } },
          },
        });
        const sorted = response.data.listPosts.items.sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setUserPosts(sorted);
      } catch (err) {
        console.error("Error fetching posts:", err);
      }
    };

    fetchUserPosts();
  }, [userId, user, isOwnProfile]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setAvatarLoading(true);
    try {
      const avatarKey = await uploadImageToS3(file, "avatars");
      await client.graphql({
        query: updateUser,
        variables: {
          input: {
            id: user.id,
            avatarUrl: avatarKey,
          },
        },
      });
      await refreshUser();
    } catch (err) {
      console.error("Error uploading avatar:", JSON.stringify(err, null, 2));
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!viewedUser?.avatarUrl || !user) return;
    setAvatarLoading(true);
    try {
      await remove({ key: viewedUser.avatarUrl });
      await client.graphql({
        query: updateUser,
        variables: {
          input: {
            id: user.id,
            avatarUrl: null,
          },
        },
      });
      await refreshUser();
    } catch (err) {
      console.error("Error deleting avatar:", err);
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (!user) return;

    const trimmedUsername = usernameInput.trim();
    if (!trimmedUsername) {
      alert("Username cannot be empty.");
      return;
    }

    try {
      const usernameChanged =
        trimmedUsername !== (viewedUser?.username || "").trim();

      await client.graphql({
        query: updateUser,
        variables: {
          input: {
            id: user.id,
            username: trimmedUsername,
            hobbies: hobbiesInput.split(",").map((s) => s.trim()),
            favoriteColors: colorsInput.split(",").map((s) => s.trim()),
            favoriteAnimal: animalInput,
            favoriteBooks: booksInput,
            favoriteTvShows: showsInput,
            favoriteMovies: moviesInput,
            favoriteGames: gamesInput,
            favoriteSnacks: snacksInput,
            favoriteDisneyRides: ridesInput,
          },
        },
      });

      if (usernameChanged && userPosts.length > 0) {
        await Promise.all(
          userPosts.map((post) =>
            client.graphql({
              query: updatePost,
              variables: {
                input: {
                  id: post.id,
                  username: trimmedUsername,
                },
              },
            })
          )
        );
        setUserPosts((prev) =>
          prev.map((post) => ({ ...post, username: trimmedUsername }))
        );
      }

      alert("Profile updated!");
      setShowEdit(false);
      await refreshUser();

      const result: any = await client.graphql({
        query: getUser,
        variables: { id: user.id },
      });
      setViewedUser(result.data.getUser);
    } catch (err) {
      console.error("Error updating profile fields:", err);
      alert("Could not update profile. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setUsernameInput(viewedUser?.username || "");
    setHobbiesInput(viewedUser?.hobbies?.join(", ") || "");
    setColorsInput(viewedUser?.favoriteColors?.join(", ") || "");
    setAnimalInput(viewedUser?.favoriteAnimal || "");
    setBooksInput(viewedUser?.favoriteBooks || "");
    setShowsInput(viewedUser?.favoriteTvShows || "");
    setMoviesInput(viewedUser?.favoriteMovies || "");
    setGamesInput(viewedUser?.favoriteGames || "");
    setSnacksInput(viewedUser?.favoriteSnacks || "");
    setRidesInput(viewedUser?.favoriteDisneyRides || "");
    setShowEdit(false);
  };

  if (loading || !viewedUser) return <div>Loading...</div>;

  return (
    <div className="profile-container">
      <div className="profile-layout">
        {/* LEFT */}
        <div className="profile-sidebar">
          <div className="profile-info">
            <h1>{viewedUser.name}</h1>
            {viewedUser.username && (
              <p className="profile-username">@{viewedUser.username}</p>
            )}
          </div>
          <div className="profile-avatar-section">
            <Avatar
              avatarUrl={viewedUser.avatarUrl}
              name={viewedUser.name}
              size={150}
              className="profile-avatar-img"
            />
            {isOwnProfile && !viewedUser.avatarUrl && (
              <label className="upload-avatar-button">
                {avatarLoading ? "Uploading..." : "Upload Photo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  hidden
                />
              </label>
            )}
            {isOwnProfile && viewedUser.avatarUrl && (
              <button
                className="upload-avatar-button"
                onClick={handleDeleteAvatar}
                disabled={avatarLoading}
              >
                {avatarLoading ? "Deleting..." : "Delete Photo"}
              </button>
            )}
          </div>

          {isOwnProfile && <SignOut />}

          <div className="profile-edit-section">
            <div className="intro-field">
              <strong>Hobbies:</strong>{" "}
              <span>{viewedUser.hobbies?.join(", ") || "Not added yet"}</span>
            </div>
            <div className="intro-field">
              <strong>Favorite Colors:</strong>{" "}
              <span>
                {viewedUser.favoriteColors?.join(", ") || "Not added yet"}
              </span>
            </div>
            <div className="intro-field">
              <strong>Favorite Animal:</strong>{" "}
              <span>
                <span>{viewedUser.favoriteAnimal || "Not added yet"}</span>
              </span>
            </div>
            <div className="intro-field">
              <strong>Favorite Book/s:</strong>{" "}
              <span>{viewedUser.favoriteBooks || "Not added yet"}</span>
            </div>
            <div className="intro-field">
              <strong>Favorite TV Show/s:</strong>{" "}
              <span>{viewedUser.favoriteTvShows || "Not added yet"}</span>
            </div>
            <div className="intro-field">
              <strong>Favorite Movie/s:</strong>{" "}
              <span>{viewedUser.favoriteMovies || "Not added yet"}</span>
            </div>
            <div className="intro-field">
              <strong>Favorite Game/s:</strong>{" "}
              <span>{viewedUser.favoriteGames || "Not added yet"}</span>
            </div>
            <div className="intro-field">
              <strong>Favorite Snack/s:</strong>{" "}
              <span>{viewedUser.favoriteSnacks || "Not added yet"}</span>
            </div>
            <div className="intro-field">
              <strong>Favorite Disney Ride/s:</strong>{" "}
              <span>{viewedUser.favoriteDisneyRides || "Not added yet"}</span>
            </div>

            {isOwnProfile && !showEdit && (
              <button
                className="upload-avatar-button"
                onClick={() => setShowEdit(true)}
              >
                Edit details
              </button>
            )}

            {isOwnProfile && showEdit && (
              <div className="intro-edit-form">
                <input
                  type="text"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  placeholder="Username"
                  aria-label="Username"
                />
                <input
                  type="text"
                  value={hobbiesInput}
                  onChange={(e) => setHobbiesInput(e.target.value)}
                  placeholder="Hobbies"
                />
                <input
                  type="text"
                  value={colorsInput}
                  onChange={(e) => setColorsInput(e.target.value)}
                  placeholder="Favorite Colors"
                />
                <input
                  type="text"
                  value={animalInput}
                  onChange={(e) => setAnimalInput(e.target.value)}
                  placeholder="Favorite Animal"
                />
                <input
                  type="text"
                  value={booksInput}
                  onChange={(e) => setBooksInput(e.target.value)}
                  placeholder="Favorite books"
                />
                <input
                  type="text"
                  value={showsInput}
                  onChange={(e) => setShowsInput(e.target.value)}
                  placeholder="Favorite TV shows"
                />
                <input
                  type="text"
                  value={moviesInput}
                  onChange={(e) => setMoviesInput(e.target.value)}
                  placeholder="Favorite movies"
                />
                <input
                  type="text"
                  value={gamesInput}
                  onChange={(e) => setGamesInput(e.target.value)}
                  placeholder="Favorite games"
                />
                <input
                  type="text"
                  value={snacksInput}
                  onChange={(e) => setSnacksInput(e.target.value)}
                  placeholder="Favorite snacks"
                />
                <input
                  type="text"
                  value={ridesInput}
                  onChange={(e) => setRidesInput(e.target.value)}
                  placeholder="Favorite Disney rides"
                />
                <div className="edit-buttons">
                  <button className="save-button" onClick={handleProfileUpdate}>
                    Save
                  </button>
                  <button
                    className="cancel-button"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div className="profile-posts">
          <h2>
            {isOwnProfile ? "Your Posts" : `${viewedUser.username}'s Posts`}
          </h2>
          {userPosts.length === 0 ? (
            <p>No posts yet.</p>
          ) : (
            userPosts.map((post) => <Post key={post.id} post={post} />)
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
